import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Tv,
  Globe,
  Play,
  ArrowRight,
  Zap,
  BarChart3,
  Building,
  AudioWaveform,
  HeadphonesIcon,
  ChevronRight,
  Shield,
  Pause,
  Clock,
  Award,
  Star,
  CheckCircle2,
  Sparkles,
  TrendingUp,
  Users,
  Video,
  Layout,
  Copy,
  Wand2,
  Instagram,
  Youtube,
  Facebook,
  Maximize2,
  CheckCircle,
} from "lucide-react";
import BackToTop from "../../components/BackToTop/BackToTop";
import TestimonialCarousel, { testimonials } from "../../components/TestimonialCarousel";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import HeroBg from "../../../src/images/hero-bg.png";
import AuthPopup from "../../components/popup/AuthPopup";

export default function Home() {
  const videoRef = useRef(null);
  const [searchParams] = useSearchParams();

  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const navigate = useNavigate();

  useEffect(() => {
    const page = searchParams.get("p");

    if (page === "signup") {
      setAuthMode("signup");
      setShowAuth(true);
    }

    if (page === "login") {
      setAuthMode("login");
      setShowAuth(true);
    }
  }, [searchParams]);


  useEffect(() => {
    const els = document.querySelectorAll(".reveal-text");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.2 }
    );

    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  useEffect(() => {
    let timeout;
    if (isPlaying) {
      timeout = setTimeout(() => setShowControls(false), 2000);
    }
    return () => clearTimeout(timeout);
  }, [isPlaying, showControls]);

  const handleToggle = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setShowControls(true);
    } else {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setShowControls(true);
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percentage * videoRef.current.duration;
      setProgress(percentage * 100);
    }
  };

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, []);

  const faqs = [
    {
      q: "What is a VAST tag and why do I need it?",
      a: "VAST (Video Ad Serving Template) is a universal format that allows video ads to be served across different platforms and devices. Your VAST tag is like a URL that tells ad servers where to find your video ad and how to display it. With SocialTV, we generate this automatically for you."
    },
    {
      q: "Can I use content from any social media platform?",
      a: "Yes! SocialTV supports imports from Instagram, YouTube, Facebook, X (Twitter), Pinterest, and Vimeo. Simply paste the URL and our AI will optimize it for TV display."
    },
    {
      q: "How long does it take to create a CTV ad?",
      a: "Most users can create a professional CTV ad in under 5 minutes. Simply choose a template, paste your social media URL, and we handle the rest. Advanced customizations may take a bit longer."
    },
    {
      q: "What platforms can I run my ads on?",
      a: "Your VAST tag works with all major CTV platforms including Roku, Amazon Fire TV, Apple TV, Samsung TV Plus, LG Channels, and any ad server that supports VAST 4.2 (Google Ad Manager, The Trade Desk, etc.)."
    },
    {
      q: "Do I need video editing experience?",
      a: "Not at all! Our AI-powered platform handles all the technical aspects. You just need your social media content and we'll transform it into a stunning TV ad automatically."
    },
    {
      q: "Is there a free trial?",
      a: "Yes! Our free plan lets you create up to 3 videos per month. No credit card required. When you're ready for more, you can upgrade to Pro for unlimited videos and 4K quality."
    }
  ];

  const stats = [
    { number: "98%", label: "Client Satisfaction", icon: Star, color: "from-amber-500 to-orange-500" },
    { number: "50M+", label: "Ads Delivered", icon: TrendingUp, color: "from-blue-500 to-cyan-500" },
    { number: "150+", label: "Enterprise Clients", icon: Building, color: "from-purple-500 to-pink-500" },
    { number: "24/7", label: "Support Available", icon: HeadphonesIcon, color: "from-green-500 to-emerald-500" },
  ];

  const steps = [
    {
      number: "01",
      title: "Choose Template",
      description: "Select from 10+ professional CTV ad templates optimized for TV screens and VAST compatibility.",
      icon: Layout,
      tags: ["Modern", "Corporate", "Dynamic"],
      color: "from-indigo-500 to-blue-600"
    },
    {
      number: "02",
      title: "Add Your Content",
      description: "Paste your video URL from YouTube, Facebook, or social media. Add your brand logo and messaging.",
      icon: Wand2,
      features: ["Auto 4K", "HDR", "60fps"],
      color: "from-purple-500 to-pink-600"
    },
    {
      number: "03",
      title: "Get VAST Tag",
      description: "Generate your VAST tag URL instantly. Copy and paste into any ad server or CTV platform.",
      icon: Copy,
      badges: ["Ready in 30s", "VAST 4.2"],
      color: "from-pink-500 to-rose-600"
    },
  ];

  const whyCards = [
    {
      icon: Tv,
      title: "Reach Big Screens",
      description: "Transform your social creatives into stunning TV ads that captivate audiences on the biggest screens.",
      stat: "2.5B+ Connected TVs",
      features: ['4K Ultra HD Quality', 'HDR10+ Support', 'Multi-Device Sync', 'Smart TV Optimized'],
      gradient: "from-blue-500 to-indigo-600",
      bgGradient: "from-blue-50 to-indigo-50",
      statBg: "bg-blue-50 text-blue-600",
      linkColor: "text-blue-600",
      borderColor: "border-blue-100"
    },
    {
      icon: Zap,
      title: "Fast Production",
      description: "Turn any social post into professional CTV ads in minutes, not days. AI handles the heavy lifting.",
      stat: "85% Faster Production",
      features: ['AI-Powered Editing', 'Auto-Formatting', 'Batch Processing', 'Template Library'],
      gradient: "from-orange-500 to-red-600",
      bgGradient: "from-orange-50 to-red-50",
      statBg: "bg-orange-50 text-orange-600",
      linkColor: "text-orange-600",
      borderColor: "border-orange-100"
    },
    {
      icon: BarChart3,
      title: "Measure Performance",
      description: "Track real-time views, engagement metrics, and ROI across all your CTV campaigns.",
      stat: "Real-time Analytics",
      features: ['Live Dashboard', 'Engagement Tracking', 'ROI Calculator', 'Audience Insights'],
      gradient: "from-green-500 to-emerald-600",
      bgGradient: "from-green-50 to-emerald-50",
      statBg: "bg-green-50 text-green-600",
      linkColor: "text-green-600",
      borderColor: "border-green-100"
    },
  ];

  return (
    <div className="bg-white">
      <BackToTop />
      <Header loginClick={() => { setShowAuth(true) }} />

      {/* ================= HERO SECTION ================= */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#f6f5ea]">
        {/* Clean Background */}
        <div className="absolute inset-0 z-0">
          {/* <img
            src={HeroBg}
            alt=""
            className="w-full h-full object-cover opacity-[0.02]"
          /> */}

          {/* Large Curved Shape */}
          <div className="absolute -top-40 -right-44 w-[800px] h-[800px] rounded-full border-[80px] border-[#ff6968]/5" />
          <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full border-[60px] border-[#ff6968]/5" />


          {/* Wave Shapes */}
          {/* <svg className="absolute bottom-0 left-0 w-full h-2/3" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="rgba(255,105,104,0.05)" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,208C960,192,1056,160,1152,149.3C1248,139,1344,149,1392,154.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
            <path fill="rgba(255,105,104,0.03)" d="M0,192L48,181.3C96,171,192,149,288,149.3C384,149,480,171,576,181.3C672,192,768,192,864,176C960,160,1056,128,1152,117.3C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
            <path fill="rgba(255,105,104,0.01)" d="M0,256L48,245.3C96,235,192,213,288,213.3C384,213,480,235,576,245.3C672,256,768,256,864,240C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
          </svg> */}

          {/* Soft gradient overlay */}
          {/* <div className="absolute inset-0 bg-gradient-to-br from-[#ff69681a] via-transparent to-[#ff69681a]" /> */}

          {/* Subtle gradient orbs - reduced size and opacity */}
          {/* <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-gradient-to-r from-[#ff6968]/10 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-r from-[#ff6968]/10 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" /> */}
        </div>

        {/* Subtle Grid Pattern */}
        {/* <div className="absolute inset-0 z-0 opacity-[0.01]">
          <div className="h-full w-full" style={{
            backgroundImage: 'radial-gradient(circle, #ff6968 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div> */}

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 lg:pt-16 lg:pb-0 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column */}
            <div className="space-y-7">
              {/* Badge */}
              <div className="flex justify-center lg:justify-start">
                <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-[#ff69681a] shadow-sm hover:shadow-md transition-all duration-300">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff6968] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ff6968]" />
                  </span>
                  <span className="text-sm font-medium text-[#27271f]">
                    🚀 New: CTV Launchpad is here
                  </span>
                  <span className="bg-[#ff6968] text-white text-[10px] px-2.5 py-0.5 rounded-full font-medium">
                    Live
                  </span>
                </div>
              </div>

              {/* Heading - Reduced size */}
              <div>
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-[#27271f]">
                  Transform Social
                  Posts into
                  {/* <br /> */}
                  <span className="block mt-1 text-[#ff6968]">
                    Stunning TV Ads
                  </span>
                </h1>
              </div>

              <p className="text-base sm:text-lg text-[#3b3b3b] max-w-lg leading-relaxed">
                Unlock your content from{" "}
                <span className="font-semibold text-[#ff6968] relative">
                  Instagram, YouTube, and Facebook
                  <span className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[#ff6968]/20" />
                </span>
                {" "}with AI-powered platform that creates premium CTV experiences in minutes.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-1">
                <button
                  onClick={() => setShowAuth(true)}
                  className="group relative inline-flex items-center gap-2.5 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] text-white px-7 py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-[#ff6968]/30 transition-all duration-300 hover:-translate-y-1 hover:scale-105 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-2.5">
                    Start Creating Free
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </button>

                <Link
                  to="/templates"
                  className="group inline-flex items-center gap-2 bg-white border border-[#ff6968]/20 text-[#27271f] px-7 py-3.5 rounded-xl font-medium hover:border-[#ff6968] hover:text-[#ff6968] transition-all duration-300"
                >
                  <Play className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  Watch Demo
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-5 pt-1">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <img
                      key={i}
                      src={`https://i.pravatar.cc/32?img=${i}`}
                      alt="User"
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform duration-300 cursor-pointer"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-[#27271f]">2,000+</span>
                    <span className="text-sm text-[#3b3b3b]">creators trust us</span>
                  </div>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-[#ff6968] text-[#ff6968]" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Video Player */}
            <div className="relative">
              {/* Subtle Glow */}
              <div className="absolute -inset-4 bg-[#ff6968]/5 rounded-2xl blur-2xl" />

              <div className="absolute -inset-8 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] rounded-3xl blur-3xl opacity-20 animate-pulse-slow" />

              {/* <div className="absolute -inset-4 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] rounded-3xl blur-3xl opacity-10 animate-pulse-slow animation-delay-2000" /> */}
              {/* <div className="absolute -inset-12 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] rounded-3xl blur-3xl opacity-5 animate-pulse-slow animation-delay-4000" /> */}

              <div className="relative">
                {/* Video Container */}
                <div className="relative bg-gradient-to-br from-[#27271f] to-[#3b3b3b] rounded-2xl p-1.5 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-white/5">
                  <div className="relative rounded-xl overflow-hidden bg-black">
                    <video
                      ref={videoRef}
                      src="https://test-pages.b-cdn.net/SocialTV/Master/layout3.mp4"
                      className="w-full h-full object-cover"
                      playsInline
                      preload="auto"
                      loop
                      autoPlay
                      muted
                      onLoadedData={() => setIsVideoLoaded(true)}
                    />

                    {/* Loading State */}
                    {!isVideoLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#27271f]">
                        <div className="w-8 h-8 border-2 border-[#ff6968] border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}

                    {/* Status Badge */}
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 bg-black/50 backdrop-blur-sm rounded-lg text-[10px] text-white/80 font-medium flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-[#ff6968] rounded-full animate-pulse" />
                        Live Demo
                      </span>
                    </div>

                    {/* Progress Bar */}
                    {/* <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
                      <div
                        className="h-full bg-[#ff6968] transition-all duration-300"
                        style={{ width: `${progress || 75}%` }}
                      />
                    </div> */}
                  </div>
                </div>

                {/* Floating Info Cards - Clean */}
                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg px-3.5 py-2.5 hidden lg:flex items-center gap-2.5 border border-[#ff69681a] animate-float">
                  <div className="w-10 h-10 bg-[#ff6968]/10 rounded-lg flex items-center justify-center">
                    <Zap className="w-6 h-6 text-[#ff6968]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#3b3b3b] uppercase tracking-wider">Processing Time</p>
                    <p className="text-md font-semibold text-[#27271f]">~30 sec</p>
                  </div>
                </div>

                <div className="absolute -bottom-6 -left-4 bg-white rounded-full px-2.5 py-1.5 shadow-lg hidden lg:flex items-center gap-2 border border-[#ff69681a]  animate-float-delayed">
                  <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center">
                    <Shield className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-md font-semibold text-[#27271f]">99.9%</span>
                    <span className="text-[10px] text-[#3b3b3b]">Uptime</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>

      </section>

      {/* ================= DEMO SECTION ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        {/* Background with your colors */}
        <div className="absolute inset-0 bg-gradient-to-br  from-[#ff6968]/5 via-transparent to-[#ff6968]/5" />

        {/* <div className="absolute inset-0 bg-gradient-to-b from-white via-[#f6f5ea] to-white" /> */}


        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Sparkles className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Interactive Demo</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-6 mb-6 text-[#27271f]">
              See the
              <span className="bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] bg-clip-text text-transparent animate-gradient"> Magic </span>
              Happen
            </h2>

            <p className="text-xl text-[#3b3b3b] leading-relaxed">
              Watch how your social media content transforms into a stunning
              <span className="font-semibold text-[#ff6968] relative group cursor-pointer">
                {" "}Connected TV experience
                <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-[#ff6968]/30 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </span>
              {" "}in real-time.
            </p>
          </div>

          {/* Video Card */}
          <div className="max-w-5xl mx-auto">
            <div className="relative group">
              {/* Animated glow effect with your color */}
              {/* <div className="absolute -inset-1 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] rounded-2xl blur-lg opacity-25 group-hover:opacity-35 transition duration-500" />
              <div className="absolute -inset-3 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition duration-700" /> */}

              <div className="relative bg-white rounded-2xl shadow-xl border border-[#ff69681a] overflow-hidden hover:shadow-3xl transition-all duration-500">
                {/* Header bar with your colors */}
                <div className="px-6 py-3  border-b border-[#ff69681a] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div class="flex gap-1.5">
                      <div class="w-3 h-3 rounded-full bg-red-400"></div>
                      <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div class="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    <div className="w-px h-5 bg-[#ff69681a]" />
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-[#ff6968] rounded flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Video className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm font-medium text-[#27271f]">SocialTV Studio</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2.5 py-1 bg-[#ff69681a] text-[#ff6968] rounded-full font-medium">4K • HDR</span>
                    <span className="text-xs px-2.5 py-1 bg-green-50 text-green-600 rounded-full font-medium flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                      Live
                    </span>
                  </div>
                </div>

                {/* Video with interactive controls */}
                <div
                  className="relative aspect-video bg-black"
                  onMouseEnter={() => isPlaying && setShowControls(true)}
                  onMouseMove={() => isPlaying && setShowControls(true)}
                  onMouseLeave={() => isPlaying && setShowControls(false)}
                >
                  <video
                    ref={videoRef}
                    src="https://test-pages.b-cdn.net/SocialTV/Master/layout5.mp4"
                    className="w-full h-full object-cover"
                    playsInline
                    preload="metadata"
                    onClick={handleToggle}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={(e) => setDuration(e.target.duration)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setShowControls(true);
                    }}
                  />

                  {/* Play button overlay with ripple effect */}
                  {(showControls || !isPlaying) && (
                    <>
                      <button
                        onClick={handleToggle}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white/95 backdrop-blur rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 z-10 group"
                      >
                        <div className="absolute inset-0 rounded-full border-2 border-[#ff6968]/20 animate-ripple" />
                        <div className="absolute inset-0 rounded-full border-2 border-[#ff6968]/10 animate-ripple animation-delay-500" />
                        {isPlaying ? (
                          <Pause className="w-7 h-7 text-[#27271f]" />
                        ) : (
                          <Play className="w-7 h-7 ml-1 text-[#ff6968]" />
                        )}
                      </button>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </>
                  )}

                  {/* Controls */}
                  {showControls && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-4 px-4">
                      <div className="flex justify-between text-xs text-white/80 mb-1.5">
                        <span className="font-mono">{formatTime(currentTime)}</span>
                        <span className="font-mono">{formatTime(duration)}</span>
                      </div>
                      <div
                        className="h-1.5 bg-white/30 rounded-full cursor-pointer group/progress hover:h-2 transition-all duration-200"
                        onClick={handleSeek}
                      >
                        <div
                          className="h-full bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] rounded-full relative transition-all duration-100"
                          style={{ width: `${progress}%` }}
                        >
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-[#ff6968] rounded-full shadow-lg opacity-0 group-hover/progress:opacity-100 transition-opacity scale-0 group-hover/progress:scale-100 transition-transform" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer with interactive elements */}
                <div className="px-6 py-5 bg-white">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    {/* <div className="flex items-center gap-4">
                      <div className="flex -space-x-2">
                        <PlatformIcon color="from-pink-500 to-purple-600" />
                        <PlatformIcon color="from-blue-500 to-cyan-500" />
                        <PlatformIcon color="from-gray-900 to-gray-700" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Instagram → YouTube → Facebook → CTV</p>
                        <p className="text-xs text-gray-500">Seamless transformation in under 30 seconds</p>
                      </div>
                    </div> */}
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-white">
                          <Instagram className="w-5 h-5" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-400 flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-white">
                          <Youtube className="w-5 h-5" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500  flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-white">
                          <Facebook className="w-5 h-5" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff6968] to-[#ff6968] flex items-center justify-center text-white text-sm font-bold shadow-lg ring-2 ring-white ring-offset-2 ring-offset-white">
                          <Tv className="w-5 h-5" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Cross-Platform Magic</p>
                        <p className="text-xs text-gray-500">Instagram → YouTube → Facebook → CTV in 30s</p>
                      </div>
                    </div>

                    <div
                      onClick={() => setShowAuth(true)}
                      className="group cursor-pointer inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] text-white rounded-full font-medium shadow-md hover:shadow-[#ff6968]/30 transition-all duration-300 hover:-translate-y-1 hover:scale-105"
                    >
                      <span>Try Now</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ================= WHY SECTION ================= */}
      <section className="relative py-24 lg:py-32 overflow-hidden hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-[#f6f5ea] to-white" />

          {/* Floating gradient orbs */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float-delayed" />
          {/* <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" /> */}

          {/* Animated particles */}
          {/* <div className="absolute inset-0">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-[#ff6968] rounded-full animate-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 10}s`,
                  animationDuration: `${10 + Math.random() * 20}s`,
                  opacity: Math.random() * 0.15 + 0.05,
                }}
              />
            ))}
          </div> */}
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header with enhanced animations */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Star className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Why Choose Us</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-[#27271f]">
              Why{" "}
              <span className="bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] bg-clip-text text-transparent animate-gradient bg-[length:200%_200%]">
                Social CTV
              </span>
            </h2>

            <p className="text-xl text-[#3b3b3b] leading-relaxed">
              The smarter way to advertise on connected TV
            </p>
          </div>

          {/* Cards Grid - Keeping your design intact */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {whyCards.map((card, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-fade-in"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Gradient background overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                {/* Glow effect on hover */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] blur-xl opacity-0 group-hover:opacity-10 transition-opacity duration-500" />

                <div className="relative p-8">
                  {/* Icon with your colors */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-xl flex items-center justify-center text-white shadow-lg mb-6 group-hover:scale-105  transition-all duration-300`}>
                    <card.icon className="w-7 h-7" />
                  </div>

                  <h3 className="text-2xl font-bold text-[#27271f] mb-3 group-hover:text-[#ff6968] transition-colors duration-300">
                    {card.title}
                  </h3>

                  <p className="text-[#3b3b3b] mb-6 leading-relaxed">{card.description}</p>

                  {/* Stat with your colors */}
                  <div className={`inline-flex items-center gap-2 ${card.statBg} px-3 py-1.5 rounded-full text-sm font-medium mb-6 group-hover:scale-105 transition-transform duration-300`}>
                    <span className="w-1.5 h-1.5 bg-[#ff6968] rounded-full animate-pulse" />
                    {card.stat}
                  </div>

                  {/* Features with hover animation */}
                  <ul className="space-y-3">
                    {card.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-[#3b3b3b] group-hover:translate-x-1 transition-transform duration-300"
                        style={{ transitionDelay: `${i * 50}ms` }}
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#ff6968] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Stats Grid with animations */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 text-center border border-[#ff69681a] hover:border-[#ff6968] hover:shadow-xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${(index + 3) * 150}ms` }}
                >
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${stat.color} mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-[#27271f] mb-1 group-hover:text-[#ff6968] transition-colors duration-300">
                    {stat.number}
                  </div>
                  <div className="text-sm text-[#3b3b3b] group-hover:text-[#27271f] transition-colors duration-300">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Enhanced CTA */}
            <div className="text-center mt-16">
              <div
                onClick={() => setShowAuth(true)}
                className="group cursor-pointer inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-[#ff6968]/30 transition-all duration-300 hover:-translate-y-1 hover:scale-105 relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-3">
                  <span>Start your free trial</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
              <p className="mt-4 text-sm text-[#3b3b3b] flex items-center justify-center gap-4">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-[#ff6968]" />
                  No credit card required
                </span>
                <span className="w-px h-4 bg-[#ff69681a]" />
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-[#ff6968]" />
                  Cancel anytime
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY SECTION - ZIG ZAG ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br  from-[#ff6968]/5 via-transparent to-[#ff6968]/5" />

          {/* Floating gradient orbs */}
          {/* <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float-delayed" /> */}
          {/* <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" /> */}

          {/* Animated particles */}
          {/* <div className="absolute inset-0">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-[#ff6968] rounded-full animate-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 10}s`,
                  animationDuration: `${10 + Math.random() * 20}s`,
                  opacity: Math.random() * 0.15 + 0.05,
                }}
              />
            ))}
          </div> */}
        </div>

        <div className="relative max-w-7xl mx-auto px-6">

          {/* <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-block text-sm font-semibold text-[#ff6968] uppercase tracking-wider mb-3">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Built for the future of
              <span className="block text-[#ff6968]">Connected TV</span>
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to create premium TV experiences from your social content.
            </p>
          </div> */}

          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Star className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Why Choose Us</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-[#27271f]">
              Built for the future of{""}
              <span className="block bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] bg-clip-text text-transparent animate-gradient bg-[length:200%_200%]">
                Connected TV
              </span>
            </h2>

            <p className="text-xl text-[#3b3b3b] leading-relaxed">
              Everything you need to create premium TV experiences from your social content.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-16">
            {whyCards.map((card, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-center group`}
              >
                {/* Icon Circle */}
                <div className="md:w-1/3 flex justify-center">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full bg-[#ff6968]/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                      <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${card.gradient} flex items-center justify-center text-white shadow-lg`}>
                        <card.icon className="w-10 h-10" />
                      </div>
                    </div>
                    {/* <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-400 rounded-full animate-ping" /> */}
                  </div>
                </div>

                {/* Content */}
                <div className="md:w-2/3">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-bold text-[#ff6968]">0{index + 1}</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#ff6968] transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {card.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {card.features.map((feature, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-sm bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                        <CheckCircle2 className="w-4 h-4 text-[#ff6968]" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            {/* <button
              onClick={() => setShowAuth(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#ff6968] text-white rounded-lg font-medium hover:bg-[#ff6968]/90 transition-all duration-200"
            >
              Get Started
              <ArrowRight className="w-4 h-4" />
            </button> */}

            <div
              onClick={() => setShowAuth(true)}
              className="group cursor-pointer inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-[#ff6968]/30 transition-all duration-300 hover:-translate-y-1 hover:scale-105 relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-3">
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY SECTION - ACCORDION LAYOUT ================= */}
      <section className="relative py-24 lg:py-32 bg-[#fafafa] hidden">
        <div className="relative max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-white px-6 py-2 rounded-full shadow-sm border border-[#ff6968]/10 mb-6">
              <span className="text-sm font-medium text-[#ff6968]">Why Social CTV</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-[#27271f] mb-4">
              Built for brands that
              <span className="text-[#ff6968]"> want more</span>
            </h2>

            <p className="text-lg text-[#3b3b3b] max-w-2xl mx-auto">
              Everything you need to launch, optimize, and scale your connected TV advertising.
            </p>
          </div>

          {/* Large Feature Cards with Stats */}
          <div className="space-y-6">
            {whyCards.map((card, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-transparent hover:border-[#ff6968]/20 cursor-pointer"
                onClick={() => {/* Toggle expansion */ }}
              >
                <div className="flex items-start gap-6">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center text-white shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <card.icon className="w-8 h-8" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-[#27271f] group-hover:text-[#ff6968] transition-colors">
                        {card.title}
                      </h3>
                      <span className={`text-sm font-medium ${card.statBg} px-4 py-1 rounded-full`}>
                        {card.stat}
                      </span>
                    </div>
                    <p className="text-[#3b3b3b] mb-4">{card.description}</p>

                    {/* Expandable features - hidden by default, shown on click/hover */}
                    <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-[#ff6968]/10">
                      {card.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-[#3b3b3b]">
                          <CheckCircle2 className="w-4 h-4 text-[#ff6968] flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <button
              onClick={() => setShowAuth(true)}
              className="group inline-flex items-center gap-3 bg-[#ff6968] text-white px-10 py-4 rounded-full font-semibold hover:bg-[#ff6968]/90 transition-all hover:scale-105 shadow-lg hover:shadow-[#ff6968]/30"
            >
              Get started now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </button>
          </div>
        </div>
      </section>


      {/* ================= HOW IT WORKS ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden hidden" >
        {/* Clean Background */}
        < div className="absolute inset-0" >
          {/* <div className="absolute inset-0 bg-gradient-to-b from-[#ff69681a] via-white to-[#ff69681a]" /> */}
          <div div class="absolute inset-0 bg-gradient-to-b from-[#ff6968]/5 via-transparent to-[#ff6968]/5" ></div>

          {/* Subtle gradient orbs */}
          {/* <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" /> */}
        </div >

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Sparkles className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Simple Process</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-[#27271f]">
              How It{" "}
              <span className="text-[#ff6968]">
                Works
              </span>
            </h2>

            <p className="text-xl text-[#3b3b3b] leading-relaxed">
              Transform your social content into stunning CTV ads in just three simple steps
            </p>
          </div>

          {/* Steps - Keeping your original card design */}
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="group relative">
                <div className="relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-[#ff6968] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                  {/* Step number */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105  transition-all duration-300`}>
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-4xl font-bold text-[#ff69681a] group-hover:text-[#ff6968] transition-colors duration-300">{step.number}</span>
                  </div>

                  <h3 className="text-2xl font-bold text-[#27271f] mb-3 group-hover:text-[#ff6968] transition-colors duration-300">{step.title}</h3>
                  <p className="text-[#3b3b3b] mb-6 leading-relaxed">{step.description}</p>

                  {/* Tags/Badges */}
                  <div className="flex flex-wrap gap-2">
                    {step.tags?.map((tag, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg group-hover:bg-[#ff69681a] group-hover:text-[#ff6968] transition-all duration-300 group-hover:translate-y-[-2px]">
                        {tag}
                      </span>
                    ))}
                    {step.features?.map((feature, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1 group-hover:bg-[#ff69681a] group-hover:text-[#ff6968] transition-all duration-300 group-hover:translate-y-[-2px]">
                        <Zap className="w-3 h-3 text-[#ff6968]" />
                        {feature}
                      </span>
                    ))}
                    {step.badges?.map((badge, i) => (
                      <span key={i} className="px-3 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded-lg group-hover:bg-[#ff6968] group-hover:text-white transition-all duration-300 group-hover:translate-y-[-2px]">
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section >

      {/* ================= HOW IT WORKS ================= */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#ff6968]/5 via-transparent to-[#ff6968]/5"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Sparkles className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Simple Process</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-[#27271f]">
              How It{" "}
              <span className="text-[#ff6968]">Works</span>
            </h2>
            <p className="text-xl text-[#3b3b3b] leading-relaxed">
              Transform your social content into stunning CTV ads in just three simple steps
            </p>
          </div>

          {/* Timeline with Alternating Layout */}
          <div className="relative">
            {/* Center Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#ff6968] via-[#ff6968]/50 to-transparent hidden md:block"></div>

            {steps.map((step, index) => (
              <div key={index} className={`relative flex flex-col md:flex-row items-center mb-12 last:mb-0 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}>
                {/* Timeline Node */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#ff6968] rounded-full border-4 border-white shadow-lg z-10 hidden md:flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{index + 1}</span>
                </div>

                {/* Content Card */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div className="group relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-[#ff6968] shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
                    <div className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-all duration-300 mb-4`}>
                      <step.icon className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-[#27271f] mb-3 group-hover:text-[#ff6968] transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-[#3b3b3b] mb-6 leading-relaxed">{step.description}</p>

                    <div className="flex flex-wrap gap-2">
                      {/* Step 1 - Tags */}
                      {step.tags?.map((tag, i) => (
                        <span
                          key={`tag-${i}`}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg
      group-hover:bg-[#ff69681a] group-hover:text-[#ff6968]
      transition-all duration-300"
                        >
                          {tag}
                        </span>
                      ))}

                      {/* Step 2 - Features */}
                      {step.features?.map((feature, i) => (
                        <span
                          key={`feature-${i}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100
      text-gray-700 text-xs font-medium rounded-lg
      group-hover:bg-[#ff69681a] group-hover:text-[#ff6968]
      transition-all duration-300"
                        >
                          <Zap className="w-3 h-3 text-[#ff6968]" />
                          {feature}
                        </span>
                      ))}

                      {/* Step 3 - Badges */}
                      {step.badges?.map((badge, i) => (
                        <span
                          key={`badge-${i}`}
                          className="px-3 py-1.5 bg-green-50 text-green-700
      text-xs font-medium rounded-lg
      group-hover:bg-[#ff6968] group-hover:text-white
      transition-all duration-300"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Empty Space for Alternating */}
                <div className="hidden md:block md:w-1/12"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="relative py-24 lg:py-32 bg-[#f6f5ea] overflow-hidden" >
        {/* Animated Background */}
        <div div className="absolute inset-0" >
          {/* <div className="absolute inset-0 bg-gradient-to-b from-white via-[#f6f5ea] to-white" />

          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" /> */}


          < div className="absolute inset-0 bg-gradient-to-b from-white via-[#f6f5ea] to-white" />

          {/* Floating gradient orbs */}
          < div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-float-delayed" />
          {/* <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" /> */}


        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header with enhanced animations */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Users className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Testimonials</span>
            </div>

            <h2 className="text-4xl md:text-5xl  font-bold mt-6 mb-4 text-[#27271f]">
              What Our{" "}
              <span className="bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] bg-clip-text text-transparent animate-gradient bg-[length:200%_200%]">
                Creators
              </span>{" "}
              Say
            </h2>

            <p className="text-xl text-[#3b3b3b] leading-relaxed">
              Join thousands of marketers and creators who transform their social content into stunning CTV ads
            </p>

            {/* Animated decorative line */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-[#ff6968] rounded-full" />
              <div className="w-2 h-2 bg-[#ff6968] rounded-full" />
              <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-[#ff6968] rounded-full" />
            </div>
          </div>

          {/* Testimonial Carousel with enhanced styling */}
          <div className="relative">
            {/* Glow effect behind carousel */}
            {/* <div className="absolute -inset-4 bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-[#ff6968]/5 rounded-3xl blur-2xl" /> */}

            <div className="relative">
              <TestimonialCarousel testimonials={testimonials} />
            </div>

            {/* Trust indicators below carousel */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8">
              <div className="flex items-center gap-3 group cursor-pointer hover:scale-105 transition-all duration-300">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <img
                      key={i}
                      src={`https://i.pravatar.cc/32?img=${i}`}
                      alt="User"
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform duration-300"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-[#ff6968] text-[#ff6968]" />
                    ))}
                  </div>
                  <span className="text-sm text-[#3b3b3b]">200+ happy creators</span>
                </div>
              </div>

              <div className="w-px h-8 bg-[#ff69681a]" />

              <div className="flex items-center gap-3 group cursor-pointer hover:scale-105 transition-all duration-300">
                <div className="w-10 h-10 bg-[#ff69681a] rounded-full flex items-center justify-center group-hover:bg-[#ff6968] transition-colors duration-300">
                  <Users className="w-5 h-5 text-[#ff6968] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#27271f]">4.9/5 Rating</div>
                  <span className="text-sm text-[#3b3b3b]">Based on 150+ reviews</span>
                </div>
              </div>

              <div className="w-px h-8 bg-[#ff69681a]" />

              <div className="flex items-center gap-3 group cursor-pointer hover:scale-105 transition-all duration-300">
                <div className="w-10 h-10 bg-[#ff69681a] rounded-full flex items-center justify-center group-hover:bg-[#ff6968] transition-colors duration-300">
                  <TrendingUp className="w-5 h-5 text-[#ff6968] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#27271f]">98% Satisfaction</div>
                  <span className="text-sm text-[#3b3b3b]">High retention rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* ================= FAQ ================= */}
      < section className="relative py-24 lg:py-32 bg-white overflow-hidden" >
        {/* Animated Background */}
        < div className="absolute inset-0" >
          {/* <div className="absolute inset-0 bg-gradient-to-b from-[#ff69681a] via-white to-[#ff69681a]" /> */}

          <div div class="absolute inset-0 bg-gradient-to-br from-[#ff6968]/5 via-white to-[#ff6968]/5" ></div>

          {/* Floating gradient orbs */}
          {/* <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#ff6968]/5 via-[#ff6968]/5 to-transparent rounded-full blur-3xl" /> */}
        </div >

        <div className="relative max-w-3xl mx-auto px-6">
          {/* Header with enhanced animations */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-[#ff69681a] px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
              <Sparkles className="w-3 h-3 text-[#ff6968]" />
              <span className="text-sm font-medium text-[#27271f]">Got Questions?</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-[#27271f]">
              Frequently Asked{" "}
              <span className="bg-gradient-to-r from-[#ff6968] via-[#ff6968] to-[#ff6968] bg-clip-text text-transparent animate-gradient bg-[length:200%_200%]">
                Questions
              </span>
            </h2>
          </div>

          {/* FAQ Items with enhanced interactions */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl border border-[#ff6968]/20 hover:border-[#ff6968] transition-all duration-300 overflow-hidden hover:shadow-lg hover:shadow-[#ff6968]/5"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left group-hover:bg-[#ff69681a] transition-colors duration-300"
                >
                  <div className="flex items-center gap-3">
                    {/* Question number indicator */}
                    {/* <span className={`text-xs font-medium w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${activeFaq === index
                      ? 'bg-[#ff6968] text-white'
                      : 'bg-[#f6f5ea] text-[#3b3b3b] group-hover:bg-[#ff69681a]'
                      }`}>
                      {index + 1}
                    </span> */}
                    <h3 className={`text-lg font-semibold pr-4 transition-colors duration-300 ${activeFaq === index
                      ? 'text-[#ff6968]'
                      : 'text-[#27271f] group-hover:text-[#ff6968]'
                      }`}>
                      {faq.q}
                    </h3>
                  </div>
                  <ChevronRight
                    className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${activeFaq === index
                      ? 'rotate-90 text-[#ff6968]'
                      : 'text-[#3b3b3b] group-hover:text-[#ff6968] group-hover:translate-x-1'
                      }`}
                  />
                </button>

                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${activeFaq === index ? 'pb-6 max-h-96' : 'max-h-0'
                    }`}
                >
                  <div className="pt-4 border-t border-[#ff69681a]">
                    <p className="text-[#3b3b3b] leading-relaxed flex items-start gap-3">
                      {/* <span className="text-[#ff6968] mt-1">•</span> */}
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA for more questions */}
          <div className="text-center mt-12">
            <p className="text-[#3b3b3b] mb-4">
              Still have questions? We're here to help!
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#ff6968] to-[#ff6968] text-white px-6 py-3 rounded-xl font-medium shadow-md hover:shadow-[#ff6968]/30 transition-all duration-300 hover:-translate-y-0.5 hover:scale-105"
            >
              <span>Contact Support</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section >

      {/* ================= CTA SECTION ================= */}
      < section className="relative py-24 lg:py-28 bg-gradient-to-br from-[#ff6968]/90 via-[#ff6968] to-rose-500/90 overflow-hidden" >
        {/* Animated Background */}
        <div div className="absolute inset-0" >
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.12),transparent_50%)]" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.08),transparent_50%)]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-white/90 text-sm font-medium mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Start Your Journey Today</span>
          </div>

          {/* Heading */}
          <h2 className="text-4xl md:text-5xl  font-bold text-white mb-6 leading-tight">
            Ready to Transform
            <br />
            <span className="relative inline-block">
              Your Content?
            </span>
          </h2>

          {/* Description */}
          <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join thousands of creators who are already turning their social media posts into stunning TV ads. Start for free today.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowAuth(true)}
              className="group inline-flex items-center gap-2.5 bg-white text-[#ff6968] px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
            >
              Start Creating Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            <Link
              to="/templates"
              className="group inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 hover:-translate-y-0.5"
            >
              <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
              Watch Demo
            </Link>
          </div>

          {/* Features */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-8 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-white/60" />
              No credit card required
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-white/60" />
              Free 3 videos/month
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-white/60" />
              Cancel anytime
            </div>
          </div>

          {/* Social Proof */}
          <div className="mt-6 flex items-center justify-center gap-6 text-white/70 text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>2,000+ creators</span>
            </div>
            <div className="w-px h-4 bg-white/20" />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-white/80 text-white/80" />
              ))}
              <span className="ml-1">4.9/5</span>
            </div>
          </div>
        </div>
      </section >


      <AuthPopup
        authMode={authMode}
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
      />

      <Footer />

      {/* Global Styles */}
      <style jsx global>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes floatDelayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: floatDelayed 3.5s ease-in-out infinite;
        }
        
        .reveal-text {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .reveal-text.active {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div >
  );
}

// ================= SUB-COMPONENTS =================

function Badge({ icon, text }) {
  return (
    <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300">
      <span className="text-indigo-500">{icon}</span>
      <span className="text-sm font-medium text-gray-700">{text}</span>
    </div>
  );
}

function FloatingCard({ className, icon, iconBg, title, value, variant = "stacked" }) {
  if (variant === "inline") {
    return (
      <div className={`absolute ${className} bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-gray-200 hidden lg:flex items-center gap-3 animate-float-delayed z-20`}>
        <div className={`w-8 h-8 bg-gradient-to-br ${iconBg} rounded-full flex items-center justify-center`}>
          {icon}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{value}</span>
          <span className="text-xs text-gray-500">{title}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`absolute ${className} bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 hidden lg:block animate-float z-20`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 bg-gradient-to-br ${iconBg} rounded-xl flex items-center justify-center`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-gray-500">{title}</p>
          <p className="text-lg font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function PlatformIcon({ color }) {
  return (
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${color} border-2 border-white shadow-md flex items-center justify-center`}>
      <Users className="w-5 h-5 text-white" />
    </div>
  );
}